import React, { Component } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import debounce from 'lodash-es/debounce';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import YearSelect from '@/shared/components/input/YearSelect';
import config from '@/config';
import api from '@/shared/api';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import OkCancelModal from '@/shared/components/OkCancelModal';
import RelationName, { getRelationName } from '@/shared/components/RelationName';
import ToastrService from '@/shared/services/ToastrService';

import Button from '@/shared/components/Button';
import { SaveIcon, ShareIcon } from '@/shared/components/Icons';
import Checkbox from '@/shared/components/input/Checkbox';
import DatePicker from '@/shared/components/input/DatePicker';
import TimeInput from '@/shared/components/input/TimeInput';
import UserSearch from '@/shared/components/input/UserSearch';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import ShareItemModal from '@/shared/components/ShareItemModal';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import handleHttpError from '@/shared/services/HandleHttpError';
import {
  getShareItemNameForTask,
  mapInvoiceStreamName,
} from '@/shared/services/ObjectDisplayService';
import guid, { emptyGuid } from '@/shared/utils/guid';
import intlShape from '@/shared/utils/intlShape';
import { falseFunc, isBeforeMinimum } from '@/shared/utils/validationHelpers';
import { toISODate } from '@/shared/utils/dateHelper';
import DataCacheService from '@/shared/services/DataCacheService';
import { getInvoiceStreamNames } from '@/shared/services/InvoiceStreamService';
import { ModalContext, ModalType } from '@/shared/providers/ModalProvider';
import { retrieveWorkResources } from '@/shared/components/registration/resourceApi';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';

import EditableTextArea from '@/shared/components/editable/EditableTextArea';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import InputGroupWrapper from '@/shared/components/InputGroupWrapper';
import { TaskTagsMultiSelect } from '@/shared/components/input/multiselect/TaskTagsMultiSelect';
import HasFeature from '@/shared/components/security/HasFeature';
import NumberInput from '@/shared/components/input/NumberInput';
import { actions } from '@/shared/components/registration/registrationHelpers';
import dateFormatService from '../../../shared/services/DateFormatService';
import { dynamicSortMultiple } from '../../../shared/utils/dynamicSort';
import TaskIcon from '../TaskIcon';
import messages from '../taskMessages';
import TaskStatusIcon from '../TaskStatusIcon';
import TaskDetailsParameterValue from './TaskDetailsParameterValue';
import TaskDetailsRegistrations from './TaskDetailsRegistrations';
import TaskDetailsSubtasks from './TaskDetailsSubTasks';
import TaskDetailsDocuments from './TaskDetailsDocuments';
import TaskDetailsInteractions from './TaskDetailsInteractions';
import TaskDetailsHistory from './TaskDetailsHistory';

momentDurationFormatSetup(moment);

const formatMinutesAsTime = minutes =>
  minutes === null || minutes === undefined
    ? ''
    : moment.duration(minutes, 'minutes').format('hh:mm', { trim: false });

const mapParameterValues = item => {
  const result = { ...item, readOnly: item.id === emptyGuid };
  if (item.valueDate && typeof item.valueDate === 'string')
    result.valueDate = moment.utc(item.valueDate);
  if (item.relationFieldType === 15)
    result.valueObject = item.valueText ? JSON.parse(item.valueText) : null;
  return result;
};
const getTaskName = (task, intl) => {
  if (!task) return null;
  if (task.name) return task.name;
  if (!task.templateId) return '';
  return intl.formatMessage(listItemMsg('tasktemplate', task.templateId, task.templateName));
};
const mapTask = (data, intl) => {
  if (!data) return null;
  return {
    ...data,
    deadline: data.deadline && moment.utc(data.deadline),
    executionDate: data.executionDate && moment.utc(data.executionDate),
    completedDate: data.completedDate && moment.utc(data.completedDate),
    name: getTaskName(data, intl),
    userId: data.userId || (data.parent && data.parent.userId) || null,
    ...(data.parameterValues
      ? {
          parameterValues: data.parameterValues.map(mapParameterValues),
        }
      : {}),
  };
};

class TaskDetailsModal extends Component {
  static contextType = ModalContext;
  // A list of ids that has been opened. These will be used to re-open the previous task when you have navigated to a parent or subtask
  taskHistory = [];
  // A list of the tasks that had changes and were updated in the back-end. Send these back to the container when the task modal is finally closed
  updatedTasks = [];
  // Documents added while the task did not exist yet in the back-end. These are linked to the task after it is created
  pendingDocuments = [];

  state = {
    task: {
      tags: [],
    },
    showHistory: false,
    focusedDateParameters: [],
    taskIds: [],
    invoiceStreams: [],
    canEditAssignment: false,
    registrations: [],
    usersForUserSearch: [],
    retrieving: true,
    hasChanged: false,
  };

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    task: PropTypes.shape({}).isRequired,
    showSendEmailButton: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
    showSendEmailButton: true,
  };

  constructor(props) {
    super(props);
    this.hasAssignments = hasAssignments();
  }

  async componentDidMount() {
    this.form = React.createRef();

    const { task, intl } = this.props;

    this.setState({ retrieving: true });
    const resources = await retrieveWorkResources(intl);
    this.setState({ resources }, () => {
      if (task.id) {
        this.taskHistory.push(task.id);
        this.openTaskDetails(task.id);
      } else {
        const { defaultTaskResourceId } = DataCacheService.getDefaultCompany();
        let defaultResource;
        if (task.parent && task.parent.resourceId) {
          defaultResource = resources.find(x => x.id === task.parent.resourceId);
        } else if (defaultTaskResourceId) {
          defaultResource = resources.find(x => x.id === defaultTaskResourceId);
        }

        this.setState({
          selectedResource: defaultResource,
          originalTaskName: task.name,
          originalUserId: task.userId,
          task: mapTask(task, intl),
          focusedDateParameters: (task.parameterValues || []).map(x => ({
            id: x.id,
            focused: false, // to be used by date pickers
          })),
          canEditAssignment: false,
          retrieving: false,
        });
      }
    });
  }

  onTaskClick = id => {
    const { hasChanged } = this.state;
    if (hasChanged) {
      return this.saveTask(false)
        .then(() => {
          this.taskHistory.push(id);
          this.openTaskDetails(id);
        })
        .catch(() => undefined); // saveTask already showed the error; stay on the current task
    }
    this.taskHistory.push(id);
    this.openTaskDetails(id);
  };

  openTaskDetails = id => {
    const { intl } = this.props;
    const { resources } = this.state;
    this.setState({
      retrieving: true,
    });
    api.base.get(`${config.appUrls.tasks}/api/plannedTasks/details/${id}`).then(
      ({ data }) => {
        this.setState(
          {
            originalTaskName: data.name,
            originalUserId: data.userId,
            task: mapTask(data, intl),
            focusedDateParameters: (data.parameterValues || []).map(x => ({
              id: x.id,
              focused: false, // to be used by date pickers
            })),
            canEditAssignment: false,
            retrieving: false,
            hasChanged: false,
            selectedResource: data.resourceId
              ? resources.find(x => x.id === data.resourceId)
              : data.parent
              ? data.parent.resourceId
                ? resources.find(x => x.id === data.parent.resourceId)
                : null
              : null,

            taskIds: [data.id, ...(data.children ? data.children.map(x => x.id) : [])],
          },
          () => {
            this.retrieveAssignments();
            this.addDefaultUserForSubtask();
            this.retrieveRegistrations();
          },
        );
      },
      error => {
        this.setState({
          retrieving: false,
        });
        handleHttpError(intl)(error);
      },
    );
  };

  addUpdatedTask = task => {
    const updatedTaskIndex = this.updatedTasks.findIndex(x => x.id === task.id);
    if (updatedTaskIndex >= 0) {
      this.updatedTasks[updatedTaskIndex] = task;
    } else {
      this.updatedTasks.push(task);
    }
  };

  reopenTaskOrCloseModal = task => {
    const { onSave } = this.props;

    this.taskHistory = this.taskHistory.filter(x => x !== task.id);

    if (!this.taskHistory || this.taskHistory.length <= 0) {
      // close the modal and return updated tasks
      onSave(this.updatedTasks);
    } else {
      const previousTask = [...this.taskHistory].pop();
      this.openTaskDetails(previousTask);
    }
    document.body.classList.remove('modal-top-open');
  };

  prepareModelForSave = () => {
    const {
      task,
      originalTaskName,
      touchedTaskName,
      touchedUserId,
      originalUserId,
      selectedResource,
      invalidDeadline,
    } = this.state;

    if (!task.name || !task.relation || !task.relation.invoiceStreamId) return;
    if (isBeforeMinimum(task.deadline) || invalidDeadline) return;
    if (task.taskLevel !== 2 && !selectedResource) return;

    const parentResourceId = task.parent ? task.parent.resourceId : null;

    const model = {
      ...task,
    };

    model.executionDate = toISODate(model.executionDate);

    model.resourceId =
      selectedResource && (!parentResourceId || parentResourceId !== selectedResource.id)
        ? selectedResource.id
        : null;

    // Needed in order to keep showing translated value if the name itself wasn't changed
    if (!touchedTaskName) {
      model.name = originalTaskName;
    }

    if (!touchedUserId) {
      model.userId = originalUserId;
    }

    return model;
  };

  previewComputedTaskParameters = () => {
    const { task } = this.state;
    // No computed parameters present, no need to do a callback
    if (!task.parameterValues.some(pv => !!pv.computedParameterType)) return Promise.resolve();

    const model = this.prepareModelForSave();
    const { intl } = this.props;
    if (task.id === undefined) {
      return Promise.resolve();
    }

    return api.base.post(`${config.appUrls.tasks}/api/plannedTasks/ComputeParameters`, model).then(
      ({ data }) => {
        // Get most recent task state
        const { task } = this.state;
        // Override values of taskComputedValues with new values
        const parameterValues = task.parameterValues.map(pv =>
          pv.computedParameterModifiedByUser ? pv : data.filter(x => pv.id === x.id)[0] || pv,
        );
        return { ...task, parameterValues };
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  onPendingDocumentAdded = document => {
    if (this.pendingDocuments.some(x => x.id === document.id)) return;
    this.pendingDocuments = [...this.pendingDocuments, document];
  };

  onPendingDocumentRemoved = document => {
    this.pendingDocuments = this.pendingDocuments.filter(x => x.id !== document.id);
  };

  linkPendingDocuments = taskId => {
    if (!taskId || this.pendingDocuments.length === 0) return Promise.resolve();

    // Clear before posting rather than after: this way a second, concurrent call (e.g. a fast
    // double-click on save) won't post the same batch twice. On failure the batch is restored so a
    // subsequent retry can still link the same documents.
    const documentsToLink = this.pendingDocuments;
    this.pendingDocuments = [];

    const documentLinks = documentsToLink.map(document => ({
      documentId: document.id,
      linkId: taskId,
      linkType: 5,
      recognizeTemporaryDocuments: true,
    }));

    return api.base
      .post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks)
      .catch(error => {
        this.pendingDocuments = [...documentsToLink, ...this.pendingDocuments];
        throw error;
      });
  };

  saveTask = (autoClose = true) => {
    this.setState({ touchedTaskName: true });
    const model = this.prepareModelForSave();
    const { intl } = this.props;
    const { task } = this.state;
    const isNewTask = task.id === undefined;

    return (isNewTask
      ? api.base.post(`${config.appUrls.tasks}/api/plannedTasks`, model)
      : api.base.put(`${config.appUrls.tasks}/api/plannedTasks`, model)
    ).then(
      ({ data }) => {
        const updatedTask = {
          scheduleRemark: task.scheduleRemark,
          ...data,
        };

        this.addUpdatedTask(updatedTask);

        if (isNewTask) {
          // the task now exists in the back-end; keep it in state so a retry (e.g. after a failed
          // document link below) updates this task instead of creating a duplicate
          this.setState({ hasChanged: false, task: mapTask(updatedTask, intl) });
        } else {
          this.setState({ hasChanged: false });
        }

        return this.linkPendingDocuments(data.id)
          .catch(error => {
            // keep the promise rejected so the modal stays open and the still-pending documents can be linked on retry
            handleHttpError(intl)(error);
            throw error;
          })
          .then(() => {
            if (autoClose) {
              ToastrService.changesSaved(intl);
              this.reopenTaskOrCloseModal(updatedTask);
            }
            return updatedTask;
          });
      },
      error => {
        // re-throw so callers never receive a resolved undefined instead of the saved task
        handleHttpError(intl)(error);
        throw error;
      },
    );
  };

  closeModal = () => {
    const { task } = this.state;
    document.body.classList.remove('modal-top-open');
    this.reopenTaskOrCloseModal(task);
  };

  enterModal = () => {
    const { task } = this.state;
    document.body.classList.add('modal-top-open');
  };

  deadlineChanged = (deadline, isValid) => {
    this.setState(({ task }) => ({
      invalidDeadline: isValid === false,
      task: {
        ...task,
        deadline,
      },
      hasChanged: true,
    }));
  };

  executionDateChanged = date => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        executionDate: date,
      },
      hasChanged: true,
    }));
  };

  executionDurationChanged = value => {
    this.setState(({ task }) => ({
      task: { ...task, executionDuration: value },
      hasChanged: true,
    }));
  };

  userChanged = user => {
    this.setState(({ task }) => ({
      touchedUserId: true,
      task: {
        ...task,
        userId: user ? user.id : null,
      },
      hasChanged: true,
    }));
  };
  referenceYearChanged = year => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        year,
      },
      hasChanged: true,
    }));
  };
  resourceChanged = resource => {
    this.setState({
      selectedResource: resource,
      hasChanged: true,
    });
  };

  relationChanged = relation => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        invoiceStreamId: relation ? relation.invoiceStreamId : undefined,
        relationId: relation ? relation.id : undefined,
        relation,
      },
      hasChanged: true,
    }));
  };

  taskNameChanged = name => {
    this.setState(({ task }) => ({
      touchedTaskName: true,
      task: {
        ...task,
        name,
      },
      hasChanged: true,
    }));
  };
  assignmentChanged = e => {
    const id = e.target.value;
    this.setState(({ invoiceStreams, task }) => {
      const stream = invoiceStreams.find(x => x.id === id);
      if (stream === null) return {};
      const {
        assignmentId = null,
        assignmentName = null,
        assignmentTemplateName = null,
        assignmentTemplateId = null,
      } = stream;
      return {
        task: {
          ...task,
          invoiceStreamId: id,
          assignmentId,
          assignmentName,
          assignmentTemplateName,
          assignmentTemplateId,
        },
        hasChanged: true,
      };
    });
  };

  statusChanged = status => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        status: Number(status),
      },
      hasChanged: true,
    }));
  };

  getUser = userId => {
    const { usersForUserSearch, task } = this.state;
    const isSameAsMainTask = !!(task.parent && task.parent.userId === userId);
    return usersForUserSearch && userId
      ? usersForUserSearch.find(x => x.id === (isSameAsMainTask ? guid.empty : userId))
      : null;
  };

  datePickerFocusChanged = (e, updatedParameter) => {
    this.setState(({ focusedDateParameters }) => ({
      focusedDateParameters: focusedDateParameters.map(item => {
        if (item.id === updatedParameter.id) {
          return { ...item, focused: e.focused };
        }
        return item;
      }),
    }));
  };

  parameterValueChanged = (value, parameter, parameterType, isValid) => {
    this.setState(
      ({ task }) => ({
        invalidParameter: isValid === false,
        task: {
          ...task,
          parameterValues: task.parameterValues.map(item => {
            if (item.id === parameter.id) {
              return {
                ...item,
                valueText:
                  parameterType === 0 ||
                  parameterType === 4 ||
                  parameterType === 5 ||
                  parameterType === 6
                    ? value
                    : null,
                valueNumeric: parameterType === 1 ? value : null,
                valueDate: parameterType === 2 ? value : null,
                valueBoolean: parameterType === 3 ? value : null,
                computedParameterModifiedByUser: !!parameter.computedParameterType,
              };
            }
            return item;
          }),
        },
        hasChanged: true,
      }),
      debounce(() => {
        this.previewComputedTaskParameters().then(task => {
          if (task) this.setState({ task });
        });
      }, 500),
    );
  };
  retrieveRegistrations = () => {
    const { taskIds } = this.state;
    const { intl } = this.props;
    this.setState({ registrations: [] });

    if (
      !AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: privileges.Read,
      }) &&
      !AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Read,
      })
    )
      return;
    api.base
      .post(`${config.appUrls.api}/api/registration/TaskRegistrations`, taskIds)
      .then(({ data }) => {
        this.setState({
          registrations: data,
        });
      }, handleHttpError(intl));
  };

  retrieveAssignments = () => {
    const { task } = this.state;

    if (!this.hasAssignments) return;
    if (task.hasSchedule || task.taskLevel === 1) return;
    const relationId = task.relationId || (task.relation && task.relation.Id);
    if (!relationId) return;
    if (
      !AuthService.hasPermission({
        functionality: functionalities.Relation,
        privilege: privileges.Read,
      })
    )
      return;

    getInvoiceStreamNames(relationId).then(data => {
      const { intl } = this.props;
      this.setState({
        invoiceStreams: (data || [])
          .map(x => ({ ...x, label: mapInvoiceStreamName(x, intl) }))
          .sort(dynamicSortMultiple('-isMain', 'label')),
        canEditAssignment: data && data.length > 1,
      });
    });
  };

  addDefaultUserForSubtask = () => {
    const { task } = this.state;
    const { intl } = this.props;

    let allUsers = [...DataCacheService.getUsers()];

    if (task.parent && task.parent.userId && task.parent.userId !== guid.empty) {
      const parentUserName = DataCacheService.getUserName(task.parent.userId);
      const mainTaskUser = {
        id: guid.empty,
        name: `${intl.formatMessage(messages['Same as parent'])} (${parentUserName})`,
      };
      allUsers = [mainTaskUser, ...allUsers];
    }

    this.setState({
      usersForUserSearch: [...allUsers],
    });
  };

  handleTasksUpdated = updatedTasks => {
    if (!updatedTasks || !updatedTasks.length) return;
    const { intl } = this.props;

    const items = updatedTasks.map(item => mapTask(item, intl));

    updatedTasks.forEach(this.addUpdatedTask);

    this.setState(({ task }) => {
      const mainTaskUpdated = items.find(x => x.id === task.id);
      const { status, isBlocked } = mainTaskUpdated || task;

      return {
        task: {
          ...task,
          status,
          isBlocked,
          children: task.children.map(row => {
            const updated = updatedTasks.find(x => x.id === row.id);
            if (!updated) return row;
            return { ...row, ...updated };
          }),
        },
      };
    });
  };

  updateScheduleRemark = value => {
    const { task: { scheduleId, scheduleRemark, id } = {} } = this.state;
    if (!scheduleId) return;
    if ((scheduleRemark || '') === (value || '')) return;
    return api.base
      .put(`${config.appUrls.tasks}/api/TaskSchedules/Remark`, {
        taskScheduleId: scheduleId,
        remark: value,
      })
      .then(
        () => {
          this.setState(({ task }) => ({
            task: id === task.id ? { ...task, scheduleRemark: value } : task,
          }));
          return true;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return Promise.reject();
        },
      );
  };
  onTagsChanged = tags => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        tags: tags.map(x => ({
          id: x,
        })),
      },
      hasChanged: true,
    }));
  };

  openMailModal = () => {
    const { onSave, onTaskMailModalSaved } = this.props;
    const { task } = this.state;

    // save task before sending the email (if user has permissions). This avoids the email being sent with missing/incorrect data about the task.
    if (
      AuthService.hasPermission({
        functionality: task.taskLevel === 2 ? 'Todo' : 'PlannedTask',
        privilege: 'Update',
      })
    ) {
      return this.saveTask(false)
        .then(updatedTask => {
          onSave([...this.updatedTasks, updatedTask]);
          // eslint-disable-next-line react/destructuring-assignment
          this.context?.setShowModalInfo({
            modalType: ModalType.TaskMail,
            task: updatedTask,
            onSave: onTaskMailModalSaved,
          });
        })
        .catch(() => undefined); // saveTask already showed the error; keep the modal open instead of opening the mail modal
    }
    return this.context?.setShowModalInfo({
      modalType: ModalType.TaskMail,
      task,
      onSave: onTaskMailModalSaved,
    });
  };

  render() {
    const { show, intl, showSendEmailButton } = this.props;
    const {
      dateFocused,
      executionDateFocused,
      task,
      touchedRelation,
      touchedTaskName,
      retrieving,
      showHistory,
      invoiceStreams,
      canEditAssignment,
      showShareItemModal,
      usersForUserSearch,
      resources,
      selectedResource,
      registrations,
      invalidDeadline,
      invalidParameter,
      taskIds,
    } = this.state;
    const selectedUser = this.getUser(task.userId);
    const createdByUser = DataCacheService.getUserName(task.createdBy);

    const canEdit = AuthService.hasPermission({
      functionality: task.taskLevel === 2 ? 'Todo' : 'PlannedTask',
      privilege: 'Update',
    });
    const taskNameClassName = classNames('form-group', {
      'has-error': touchedTaskName && !task.name,
    });

    const relationClassName = classNames('form-group', {
      'has-error': touchedRelation && (!task.relation || !task.relation.invoiceStreamId),
    });
    const relationId = task?.relation?.id;
    const hasJointTaxReturn = (task?.parameterValues || []).some(
      p => p.relationFieldType === 41 && p.valueBoolean === true,
    );

    return (
      <OkCancelModal
        title={
          <span>
            <TaskIcon taskLevel={task.taskLevel} /> {task.name}
          </span>
        }
        show={show}
        onClose={this.closeModal}
        disabled={!canEdit || invalidDeadline || invalidParameter}
        onSave={() => this.saveTask().catch(() => undefined)} // saveTask already showed the error; keep the modal open for a retry
        size="xl"
        customClassName="modal-top"
        onEnter={this.enterModal}
        okLabel={
          <React.Fragment>
            <SaveIcon className="margin5R" /> {intl.formatMessage(messages.Save)}
          </React.Fragment>
        }
        leftButton={
          <Button onClick={() => this.setState({ showShareItemModal: true })}>
            <ShareIcon className="margin5R" /> {intl.formatMessage(messages.Share)}
          </Button>
        }
      >
        {show && retrieving && <LoadingAnimation />}

        {show && !retrieving && (
          <div className="row">
            <div className="col-md-6 border-right">
              <form ref={this.form}>
                <div className="form-horizontal">
                  {task.parent && (
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        {intl.formatMessage(messages.MainTask)}
                      </label>
                      <div
                        className="col-sm-8 col-md-9 text-clickable"
                        title={intl.formatMessage(messages.ViewTask)}
                        onClick={() => this.onTaskClick(task.parent.id)}
                      >
                        <p className="form-control-static">
                          <TaskIcon taskLevel={task.parent.taskLevel} />
                          &nbsp;
                          <span>
                            {task.parent.name && task.parent.name}
                            {!task.parent.name && (
                              <FormattedListValue
                                list="tasktemplate"
                                id={task.parent.templateId}
                                fallback={
                                  task.parent.name ||
                                  task.parent.templateName ||
                                  task.parent.taskTemplateId
                                }
                              />
                            )}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className={relationClassName}>
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.Relation} />
                    </label>
                    <div className="col-sm-8 col-md-9">
                      <p className="form-control-static">
                        <RelationName relation={task.relation} />
                      </p>
                    </div>
                  </div>
                  {this.hasAssignments && (
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.Assignment} />
                      </label>
                      <div className="col-sm-8 col-md-9">
                        {canEditAssignment ? (
                          <select
                            className="form-control "
                            value={task.invoiceStreamId}
                            onChange={this.assignmentChanged}
                          >
                            {invoiceStreams.map(x => (
                              <option key={x.id} value={x.id}>
                                {x.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="form-control-static">{mapInvoiceStreamName(task, intl)}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className={taskNameClassName}>
                    <label className="col-sm-4 col-md-3">
                      {intl.formatMessage(messages.Description)}
                    </label>
                    <div className="col-sm-8 col-md-9">
                      <input
                        type="text"
                        value={task.name}
                        maxLength="500"
                        placeholder={intl.formatMessage(messages.Description)}
                        onChange={e => this.taskNameChanged(e.target.value)}
                        required
                        disabled={!canEdit}
                        className="form-control input-sm"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.TaskStatus} />
                    </label>
                    <div className="col-sm-8 col-md-9">
                      <select
                        className="form-control input-sm"
                        required
                        disabled={!canEdit}
                        value={task.status}
                        onChange={e => this.statusChanged(e.target.value)}
                      >
                        <option value={0}>
                          {intl.formatMessage(listItemMsg('taskstatus', '0'))}
                        </option>
                        <option value={1}>
                          {intl.formatMessage(listItemMsg('taskstatus', '1'))}
                        </option>
                        <option value={2}>
                          {intl.formatMessage(listItemMsg('taskstatus', '2'))}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.AssignedTo} />
                    </label>
                    <div className="col-sm-8 col-md-9">
                      <UserSearch
                        disabled={!canEdit}
                        users={usersForUserSearch}
                        onChange={this.userChanged}
                        value={selectedUser}
                        hideClearButton
                        withUnassigned={!task.parent}
                        small
                      />
                    </div>
                  </div>
                  <div
                    className={classNames('form-group', {
                      'has-error': task.taskLevel !== 2 && !selectedResource,
                    })}
                  >
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.ItemFromPricelist} />
                    </label>
                    <div className="col-sm-8 col-md-9">
                      <ResourceAutoComplete
                        items={resources}
                        onChange={this.resourceChanged}
                        value={selectedResource}
                        required={task.taskLevel !== 2}
                        crmType={task.relation?.crm}
                        assignmentTemplateId={task.assignmentTemplateId}
                        intl={intl}
                        small
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                  <HasFeature feature="ReferenceYear">
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.ReferenceYear} />
                      </label>
                      <div className="col-sm-8 col-md-9">
                        {task.taskLevel === 0 ? (
                          <YearSelect
                            id="referenceYearField"
                            disabled={!canEdit}
                            value={task.year}
                            onChange={this.referenceYearChanged}
                            className="form-control"
                          />
                        ) : task.year === 0 ? null : (
                          task.year
                        )}
                      </div>
                    </div>
                  </HasFeature>
                  {(() => {
                    const showRemainingDuration =
                      (task.taskLevel === 0 || task.taskLevel === 1) &&
                      AuthService.hasFeature('CapacityPlanning');
                    return (
                      <div
                        className={classNames('form-group', {
                          'has-error': isBeforeMinimum(task.deadline) || invalidDeadline,
                        })}
                      >
                        <label className="col-sm-4 col-md-3">
                          <FormattedMessage {...messages.Deadline} />
                        </label>
                        <div
                          className={
                            showRemainingDuration ? 'col-sm-2 col-md-3' : 'col-sm-8 col-md-9'
                          }
                        >
                          <InputGroupWrapper
                            rightClassName="input-group-btn"
                            right={
                              task.taskLevel === 0 &&
                              task.originalDeadline &&
                              !moment
                                .utc(task.originalDeadline)
                                .isSame(moment.utc(task.deadline), 'd') ? (
                                <button
                                  type="button"
                                  className="btn btn-default"
                                  onClick={() =>
                                    this.deadlineChanged(moment.utc(task.originalDeadline))
                                  }
                                  title={intl.formatMessage(messages.RestoreOriginalDeadline, {
                                    originalDeadline: dateFormatService.formatDate(
                                      task.originalDeadline,
                                    ),
                                  })}
                                >
                                  <span className="fa fa-undo" />
                                </button>
                              ) : null
                            }
                          >
                            <DatePicker
                              placeholder={intl.formatMessage(messages.Deadline)}
                              disabled={!canEdit}
                              date={task.deadline}
                              onDateChange={this.deadlineChanged}
                              focused={dateFocused}
                              onFocusChange={({ focused }) =>
                                this.setState({ dateFocused: focused })
                              }
                              id="taskDeadline"
                              block
                              small
                              isOutsideRange={falseFunc}
                            />
                          </InputGroupWrapper>
                        </div>
                        {showRemainingDuration && (
                          <>
                            <label className="col-sm-4 col-md-3">
                              <FormattedMessage {...messages.RemainingDuration} />
                              <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement="bottom"
                                rootClose
                                overlay={
                                  <Popover id="popover-remainingDuration">
                                    {intl.formatMessage(messages.RemainingDurationTooltip)}
                                  </Popover>
                                }
                              >
                                <span className="explanation-icon margin5L">
                                  <FaInfoCircle />
                                </span>
                              </OverlayTrigger>
                            </label>
                            <div className="col-sm-2 col-md-3">
                              <span className="form-control-static" id="remainingDuration">
                                {formatMinutesAsTime(task.remainingDuration)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  <HasFeature feature="CapacityPlanning">
                    <div
                      className={classNames('form-group', {
                        'has-error': false,
                      })}
                    >
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.ExecutionDate} />
                      </label>
                      <div className="col-sm-2 col-md-3">
                        <DatePicker
                          placeholder={intl.formatMessage(messages.ExecutionDate)}
                          disabled={!canEdit}
                          date={task.executionDate}
                          onDateChange={this.executionDateChanged}
                          focused={executionDateFocused}
                          onFocusChange={({ focused }) =>
                            this.setState({ executionDateFocused: focused })
                          }
                          id="taskExecutionDate"
                          block
                          small
                          isOutsideRange={falseFunc}
                        />
                      </div>
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.ExecutionDuration} />
                      </label>
                      <div className="col-sm-2 col-md-3">
                        <TimeInput
                          className="form-control"
                          id="executionDuration"
                          disabled={!canEdit}
                          value={task.executionDuration}
                          onChange={value => this.executionDurationChanged(value)}
                          placeholder={intl.formatMessage(messages.ExecutionDuration)}
                          allowNull
                        />
                      </div>
                    </div>
                  </HasFeature>
                  {task.taskLevel === 2 && !!createdByUser && (
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.CreatedBy} />
                      </label>
                      <div className="col-sm-8 col-md-9">
                        <span className="form-control-static">{createdByUser}</span>
                      </div>
                    </div>
                  )}
                  <div className="form-group tag-multiselect">
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.Tags} />
                    </label>
                    <div className="col-sm-8 col-md-9">
                      {task && task.tags ? (
                        <TaskTagsMultiSelect
                          allowNew
                          selection={task.tags?.map(x => x.id)}
                          onChange={this.onTagsChanged}
                          disabled={!canEdit}
                          small
                        />
                      ) : null}
                    </div>
                  </div>
                  {task.completedDate && (
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.CompletedDate} />
                      </label>
                      <div className="col-sm-8 col-md-9">
                        <input
                          type="text"
                          readOnly
                          id="taskCompletedDate"
                          className="form-control input-sm"
                          value={dateFormatService.formatDate(task.completedDate)}
                        />
                      </div>
                    </div>
                  )}

                  {task && (task.taskLevel === 0 || task.taskLevel === 1) && (
                    <>
                      <div className="form-group">
                        <label htmlFor="inapplicableCheckbox" className="col-sm-4 col-md-3">
                          <FormattedMessage {...messages.Inapplicable} />
                        </label>
                        <div className="col-sm-8 col-md-9">
                          <div className="checkbox">
                            <Checkbox
                              disabled={!canEdit}
                              id="inapplicableCheckbox"
                              value={task.inapplicable}
                              onClick={e =>
                                this.setState({
                                  task: {
                                    ...task,
                                    inapplicable: e,
                                  },
                                  hasChanged: true,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="inapplicableCheckbox" className="col-sm-4 col-md-3">
                          <FormattedMessage {...messages.Rejected} />
                        </label>
                        <div className="col-sm-8 col-md-9">
                          <div className="checkbox">
                            <Checkbox
                              disabled={!canEdit}
                              id="rejectedCheckbox"
                              value={task.rejected}
                              onClick={e =>
                                this.setState({
                                  task: {
                                    ...task,
                                    rejected: e,
                                  },
                                  hasChanged: true,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {task &&
                    task.parameterValues &&
                    task.parameterValues.map(parameter => (
                      <TaskDetailsParameterValue
                        disabled={!canEdit}
                        parameter={parameter}
                        key={`${parameter.parameterDefinitionId}-${parameter.id}`}
                        onChange={this.parameterValueChanged}
                      />
                    ))}

                  {task.scheduleId && (
                    <div className="form-group">
                      <label className="col-sm-4 col-md-3">
                        <FormattedMessage {...messages.TaskScheduleRemark} />
                        <ZendeskHelp article="9530156446108" className="margin10R" />
                      </label>

                      <div className="col-sm-8 col-md-9">
                        <p className="form-control-static">
                          <EditableTextArea
                            value={task.scheduleRemark}
                            onChange={value => this.updateScheduleRemark(value)}
                            maxLength="4000"
                            rows={10}
                            readOnly={!canEdit}
                            displayValue={
                              task.scheduleRemark ? (
                                <div className="bold linebreak">{task.scheduleRemark}</div>
                              ) : null
                            }
                            style={{ minHeight: '100px' }}
                          />
                        </p>
                      </div>
                    </div>
                  )}

                  {/* REMARK */}
                  <div className="form-group">
                    <label className="col-sm-4 col-md-3">
                      <FormattedMessage {...messages.Remark} />
                    </label>

                    <div className="col-sm-8 col-md-9">
                      <textarea
                        id="RemarkField"
                        disabled={!canEdit}
                        style={{ minHeight: '100px' }}
                        rows="10"
                        maxLength="4000"
                        className="form-control input-sm vertical-resize"
                        value={task.remark || ''}
                        onChange={e =>
                          this.setState({
                            task: {
                              ...task,
                              remark: e.target.value,
                            },
                            hasChanged: true,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-md-6">
              {showHistory && (
                <TaskDetailsHistory
                  taskStatusHistory={task.taskStatusHistory}
                  taskLevel={task.taskLevel}
                  onHideHistory={() => this.setState({ showHistory: false })}
                />
              )}
              {!showHistory && (
                <TaskDetailsSubtasks
                  task={task}
                  openTaskDetails={this.onTaskClick}
                  onTasksUpdated={this.handleTasksUpdated}
                  onShowHistory={() => this.setState({ showHistory: true })}
                  historyActive={showHistory}
                  disabled={!canEdit}
                />
              )}

              <TaskDetailsRegistrations
                task={task}
                registrations={registrations}
                onShowHistory={() => this.setState({ showHistory: true })}
                historyActive={showHistory}
                disabled={!canEdit}
              />
              <TaskDetailsDocuments
                intl={intl}
                taskId={task?.id}
                taskIds={taskIds}
                relationId={relationId}
                onOpenMailModal={this.openMailModal}
                onPendingDocumentAdded={this.onPendingDocumentAdded}
                onPendingDocumentRemoved={this.onPendingDocumentRemoved}
                disabled={!canEdit}
                showSendEmailButton={showSendEmailButton}
                initialLinkTypes={hasJointTaxReturn ? [5] : []}
              />
              <TaskDetailsInteractions
                intl={intl}
                taskId={task?.id}
                taskIds={taskIds}
                relationId={relationId}
                disabled={!canEdit}
              />
              {showShareItemModal && (
                <ShareItemModal
                  show
                  type={task.taskLevel === 2 ? 'todo' : 'task'}
                  id={task.id}
                  itemName={getShareItemNameForTask(task, intl)}
                  relationId={task.relationId}
                  relationName={getRelationName(intl, task.relation)}
                  onClose={() => this.setState({ showShareItemModal: false })}
                />
              )}
            </div>
          </div>
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(TaskDetailsModal);
